import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Landmark, LogOut, Loader2, Plus, Trash2, UploadCloud, FileCheck2, Users,
  FileArchive, Fingerprint, Check, ArrowRight, ShieldCheck, Lock,
} from "lucide-react";
import { toast } from "sonner";
import { Reveal, Eyebrow } from "@/components/Reveal";
import { useTribeAuth } from "@/context/TribeAuthContext";
import api from "@/lib/api";

const PLANS = [
  { lookup: "tribal_repository_monthly", title: "Monthly", price: "$999", period: "/ month" },
  { lookup: "tribal_repository_yearly", title: "Annual", price: "$9,999", period: "/ year", highlight: true },
];

function SubscribeGate({ tribe }) {
  const [busy, setBusy] = useState(null);
  const [params] = useSearchParams();
  const autoPlan = params.get("plan");

  const subscribe = useCallback(async (lookup_key) => {
    setBusy(lookup_key);
    try {
      const res = await api.post("/tribe/subscription/checkout", { lookup_key, origin_url: window.location.origin });
      window.location.href = res.data.checkout_url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Unable to start checkout.");
      setBusy(null);
    }
  }, []);

  useEffect(() => {
    if (autoPlan && PLANS.some((p) => p.lookup === autoPlan)) {
      subscribe(autoPlan);
    }
  }, [autoPlan, subscribe]);

  return (
    <div className="max-w-5xl mx-auto px-6 lg:px-10 py-16" data-testid="tribe-subscribe-gate">
      <Reveal>
        <div className="border border-gold/25 bg-ink-surface p-8 text-center">
          <Lock size={28} className="text-gold mx-auto" strokeWidth={1.3} />
          <h2 className="font-serif text-3xl mt-5">Activate Your Repository</h2>
          <p className="font-sans text-sm text-[#A19D98] leading-relaxed mt-4 max-w-lg mx-auto">
            Welcome, <span className="text-gold-text">{tribe.tribe_name}</span>. An active subscription is
            required to access your nation's tribal rolls and document repository. Choose an arrangement to begin.
          </p>
        </div>
      </Reveal>
      <div className="grid md:grid-cols-2 gap-8 mt-10">
        {PLANS.map((p, i) => (
          <Reveal key={p.title} delay={i * 0.1}>
            <div className={`border p-8 sm:p-10 h-full flex flex-col ${p.highlight ? "border-gold/50 bg-ink-surface" : "border-gold/15 bg-ink"}`}>
              <h3 className="font-serif text-3xl">{p.title}</h3>
              <div className="flex items-baseline gap-2 mt-4">
                <span className="font-serif text-4xl text-gold-gradient">{p.price}</span>
                <span className="font-mono text-[11px] tracking-[0.14em] uppercase text-[#7a766f]">{p.period}</span>
              </div>
              <ul className="mt-8 space-y-4 flex-1">
                {["Unlimited tribal roll entries", "Governing document uploads", "Blockchain-anchored records", "Dedicated nation portal"].map((f) => (
                  <li key={f} className="flex items-start gap-3">
                    <Check size={16} className="text-gold mt-0.5 shrink-0" />
                    <span className="font-sans text-sm text-[#C9C4BD]">{f}</span>
                  </li>
                ))}
              </ul>
              <button
                data-testid={`subscribe-${p.title.toLowerCase()}`}
                disabled={busy}
                onClick={() => subscribe(p.lookup)}
                className="mt-10 flex items-center justify-center gap-3 px-6 py-4 bg-gold text-black font-mono text-[11px] tracking-[0.2em] uppercase hover:bg-gold-text transition-colors disabled:opacity-60"
              >
                {busy === p.lookup ? <><Loader2 size={16} className="animate-spin" /> Redirecting…</> : <>Subscribe {p.title} <ArrowRight size={14} /></>}
              </button>
            </div>
          </Reveal>
        ))}
      </div>
      <p className="font-sans text-xs text-[#7a766f] text-center mt-8">
        For indefinite / perpetual arrangements, <a href="mailto:7gentrust@niijiglobal.com" className="text-gold hover:underline">contact us directly</a>.
      </p>
    </div>
  );
}

function RollsPanel() {
  const [rolls, setRolls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ full_name: "", lineage: "", enrollment_number: "", dob: "", status: "Enrolled", notes: "" });
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const load = useCallback(async () => {
    try {
      const res = await api.get("/tribe/rolls");
      setRolls(res.data);
    } catch (e) { console.error("Failed to load rolls:", e); } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const add = async (e) => {
    e.preventDefault();
    if (!form.full_name) { toast.error("Full name is required."); return; }
    setBusy(true);
    try {
      await api.post("/tribe/rolls", form);
      toast.success("Citizen anchored to the registry.");
      setForm({ full_name: "", lineage: "", enrollment_number: "", dob: "", status: "Enrolled", notes: "" });
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Unable to add entry.");
    } finally { setBusy(false); }
  };

  const remove = async (id) => {
    try { await api.delete(`/tribe/rolls/${id}`); setRolls(rolls.filter((r) => r.id !== id)); toast.success("Entry removed."); }
    catch (e) { toast.error("Unable to remove."); }
  };

  const inputCls = "w-full bg-transparent border-b border-gold/30 focus:border-gold outline-none py-2.5 mt-1.5 font-sans text-sm text-[#F5F0EB] placeholder:text-[#5f5c57] transition-colors";

  return (
    <div className="grid lg:grid-cols-12 gap-10">
      <div className="lg:col-span-4">
        <form onSubmit={add} className="border border-gold/20 bg-ink-surface p-7 lg:sticky lg:top-28" data-testid="roll-form">
          <Eyebrow>Add to Registry</Eyebrow>
          <h3 className="font-serif text-2xl mt-3 mb-5">New Citizen Entry</h3>
          <div className="space-y-4">
            <div>
              <label className="font-mono text-[9px] tracking-[0.22em] uppercase text-gold/60">Full Name *</label>
              <input data-testid="roll-name" value={form.full_name} onChange={set("full_name")} className={inputCls} placeholder="Citizen full name" />
            </div>
            <div>
              <label className="font-mono text-[9px] tracking-[0.22em] uppercase text-gold/60">Lineage / Clan</label>
              <input data-testid="roll-lineage" value={form.lineage} onChange={set("lineage")} className={inputCls} placeholder="e.g. Maternal — Bear Clan" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="font-mono text-[9px] tracking-[0.22em] uppercase text-gold/60">Enrollment #</label>
                <input data-testid="roll-enrollment" value={form.enrollment_number} onChange={set("enrollment_number")} className={inputCls} placeholder="0000" />
              </div>
              <div>
                <label className="font-mono text-[9px] tracking-[0.22em] uppercase text-gold/60">Date of Birth</label>
                <input data-testid="roll-dob" value={form.dob} onChange={set("dob")} className={inputCls} placeholder="DD MMM YYYY" />
              </div>
            </div>
            <div>
              <label className="font-mono text-[9px] tracking-[0.22em] uppercase text-gold/60">Notes</label>
              <input data-testid="roll-notes" value={form.notes} onChange={set("notes")} className={inputCls} placeholder="(optional)" />
            </div>
            <button type="submit" disabled={busy} data-testid="roll-submit" className="w-full flex items-center justify-center gap-2 px-5 py-3 bg-gold text-black font-mono text-[10px] tracking-[0.2em] uppercase hover:bg-gold-text transition-colors disabled:opacity-60">
              {busy ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />} Anchor Citizen
            </button>
          </div>
        </form>
      </div>
      <div className="lg:col-span-8">
        {loading ? (
          <p className="font-mono text-xs tracking-[0.2em] uppercase text-gold/60 animate-pulse">Loading registry…</p>
        ) : rolls.length === 0 ? (
          <div className="border border-dashed border-gold/20 p-12 text-center">
            <Users size={28} className="text-gold/50 mx-auto" strokeWidth={1.3} />
            <p className="font-sans text-sm text-[#A19D98] mt-4">No citizens on the roll yet. Add your first entry.</p>
          </div>
        ) : (
          <div className="border border-gold/15 divide-y divide-gold/10" data-testid="rolls-list">
            {rolls.map((r) => (
              <div key={r.id} className="flex items-start justify-between gap-4 p-5 bg-ink-surface">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-baseline gap-x-3">
                    <h4 className="font-serif text-lg text-[#F5F0EB]">{r.full_name}</h4>
                    <span className="font-mono text-[9px] tracking-[0.18em] uppercase text-gold/60">{r.status}</span>
                  </div>
                  <div className="flex flex-wrap gap-x-5 gap-y-1 mt-2 font-mono text-[10px] text-[#A19D98]">
                    {r.enrollment_number && <span>ENR #{r.enrollment_number}</span>}
                    {r.dob && <span>DOB {r.dob}</span>}
                    {r.lineage && <span className="text-[#C9C4BD]">{r.lineage}</span>}
                  </div>
                  <p className="font-mono text-[9px] text-gold/50 mt-2 truncate">⛓ {r.block_anchor?.slice(0, 26)}… · block #{r.block_number}</p>
                </div>
                <button data-testid={`roll-delete-${r.id}`} onClick={() => remove(r.id)} className="text-[#7a766f] hover:text-red-400 transition-colors shrink-0" aria-label="Delete">
                  <Trash2 size={16} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function DocsPanel() {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [file, setFile] = useState(null);
  const [title, setTitle] = useState("");
  const [category, setCategory] = useState("Governing Document");
  const inputRef = useRef(null);

  const load = useCallback(async () => {
    try { const res = await api.get("/tribe/documents"); setDocs(res.data); }
    catch (e) { console.error("Failed to load documents:", e); } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const upload = async (e) => {
    e.preventDefault();
    if (!file || !title) { toast.error("Provide a title and a document."); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("title", title);
      fd.append("category", category);
      await api.post("/tribe/documents", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Document anchored to the repository.");
      setFile(null); setTitle("");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed.");
    } finally { setBusy(false); }
  };

  const remove = async (id) => {
    try { await api.delete(`/tribe/documents/${id}`); setDocs(docs.filter((d) => d.id !== id)); toast.success("Document removed."); }
    catch (e) { toast.error("Unable to remove."); }
  };

  const inputCls = "w-full bg-transparent border-b border-gold/30 focus:border-gold outline-none py-2.5 mt-1.5 font-sans text-sm text-[#F5F0EB] placeholder:text-[#5f5c57] transition-colors";

  return (
    <div className="grid lg:grid-cols-12 gap-10">
      <div className="lg:col-span-4">
        <form onSubmit={upload} className="border border-gold/20 bg-ink-surface p-7 lg:sticky lg:top-28" data-testid="doc-form">
          <Eyebrow>Preserve a Record</Eyebrow>
          <h3 className="font-serif text-2xl mt-3 mb-5">Upload Document</h3>
          <div className="space-y-4">
            <div>
              <label className="font-mono text-[9px] tracking-[0.22em] uppercase text-gold/60">Title *</label>
              <input data-testid="doc-title" value={title} onChange={(e) => setTitle(e.target.value)} className={inputCls} placeholder="e.g. Nation Constitution 2026" />
            </div>
            <div>
              <label className="font-mono text-[9px] tracking-[0.22em] uppercase text-gold/60">Category</label>
              <select data-testid="doc-category" value={category} onChange={(e) => setCategory(e.target.value)} className={`${inputCls} appearance-none cursor-pointer`}>
                {["Governing Document", "Treaty", "Census", "Resolution", "Tribal Roll", "Other"].map((c) => <option key={c} value={c} className="bg-ink text-[#F5F0EB]">{c}</option>)}
              </select>
            </div>
            <div onClick={() => inputRef.current?.click()} data-testid="doc-dropzone" className="border border-dashed border-gold/30 hover:border-gold/60 cursor-pointer p-6 text-center transition-colors">
              <input ref={inputRef} type="file" data-testid="doc-file" accept=".pdf,.png,.jpg,.jpeg,.webp,.csv,.txt,.doc,.docx" className="hidden" onChange={(e) => setFile(e.target.files?.[0] || null)} />
              {file ? (
                <div className="flex items-center justify-center gap-2 text-gold"><FileCheck2 size={18} /><span className="font-mono text-[11px] truncate">{file.name}</span></div>
              ) : (
                <><UploadCloud size={24} className="text-gold/70 mx-auto" strokeWidth={1.3} /><p className="font-sans text-xs text-[#C9C4BD] mt-2">Click to browse</p></>
              )}
            </div>
            <button type="submit" disabled={busy} data-testid="doc-submit" className="w-full flex items-center justify-center gap-2 px-5 py-3 bg-gold text-black font-mono text-[10px] tracking-[0.2em] uppercase hover:bg-gold-text transition-colors disabled:opacity-60">
              {busy ? <Loader2 size={14} className="animate-spin" /> : <Fingerprint size={14} />} Anchor Document
            </button>
          </div>
        </form>
      </div>
      <div className="lg:col-span-8">
        {loading ? (
          <p className="font-mono text-xs tracking-[0.2em] uppercase text-gold/60 animate-pulse">Loading repository…</p>
        ) : docs.length === 0 ? (
          <div className="border border-dashed border-gold/20 p-12 text-center">
            <FileArchive size={28} className="text-gold/50 mx-auto" strokeWidth={1.3} />
            <p className="font-sans text-sm text-[#A19D98] mt-4">No documents preserved yet. Upload your first record.</p>
          </div>
        ) : (
          <div className="border border-gold/15 divide-y divide-gold/10" data-testid="docs-list">
            {docs.map((d) => (
              <div key={d.id} className="flex items-start justify-between gap-4 p-5 bg-ink-surface">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-baseline gap-x-3">
                    <h4 className="font-serif text-lg text-[#F5F0EB] truncate">{d.title}</h4>
                    <span className="font-mono text-[9px] tracking-[0.18em] uppercase text-gold/60">{d.category}</span>
                  </div>
                  <p className="font-mono text-[10px] text-[#A19D98] mt-1 truncate">{d.original_filename} · {(d.size / 1024).toFixed(0)} KB</p>
                  <p className="font-mono text-[9px] text-gold/50 mt-2 truncate">⛓ {d.block_anchor?.slice(0, 26)}… · block #{d.block_number}</p>
                </div>
                <button data-testid={`doc-delete-${d.id}`} onClick={() => remove(d.id)} className="text-[#7a766f] hover:text-red-400 transition-colors shrink-0" aria-label="Delete">
                  <Trash2 size={16} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default function TribeDashboard() {
  const { tribe, logout, refresh } = useTribeAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("rolls");
  const [stats, setStats] = useState(null);

  const active = tribe && tribe.subscription_status === "active";

  useEffect(() => {
    if (active) api.get("/tribe/stats").then((r) => setStats(r.data)).catch((e) => console.error("Failed to load stats:", e));
  }, [active]);

  const doLogout = async () => { await logout(); navigate("/tribal-portal"); };

  if (!tribe) return null;

  return (
    <div className="min-h-screen bg-ink pt-28 pb-24">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="flex flex-wrap items-start justify-between gap-6 border-b border-gold/15 pb-8">
          <div>
            <Eyebrow>Tribal Records Repository</Eyebrow>
            <h1 className="font-serif font-light text-4xl sm:text-5xl tracking-tight mt-3 flex items-center gap-4">
              <Landmark size={34} className="text-gold" strokeWidth={1.2} /> {tribe.tribe_name}
            </h1>
            <p className="font-mono text-[11px] tracking-[0.2em] uppercase text-gold/60 mt-3">
              {tribe.nation || "Sovereign Nation"} · {active ? `${tribe.subscription_plan} Subscription — Active` : "Subscription Inactive"}
            </p>
          </div>
          <button data-testid="tribe-logout" onClick={doLogout} className="flex items-center gap-2 px-5 py-2.5 border border-gold/40 text-gold hover:bg-gold hover:text-black transition-colors font-mono text-[10px] tracking-[0.2em] uppercase">
            <LogOut size={13} /> Sign Out
          </button>
        </div>
      </div>

      {!active ? (
        <SubscribeGate tribe={tribe} />
      ) : (
        <div className="max-w-7xl mx-auto px-6 lg:px-10 mt-10">
          {stats && (
            <div className="grid sm:grid-cols-3 gap-px bg-gold/10 border border-gold/15 mb-10">
              {[
                { icon: Users, label: "Citizens on Roll", value: stats.rolls },
                { icon: FileArchive, label: "Documents Preserved", value: stats.documents },
                { icon: ShieldCheck, label: "Registry Anchor", value: `${stats.registry_anchor?.slice(0, 12)}…`, mono: true },
              ].map((s) => (
                <div key={s.label} className="bg-ink p-6">
                  <s.icon size={20} className="text-gold" strokeWidth={1.3} />
                  <p className={`mt-4 ${s.mono ? "font-mono text-lg" : "font-serif text-3xl"} text-gold-text`}>{s.value}</p>
                  <p className="font-mono text-[9px] tracking-[0.2em] uppercase text-[#7a766f] mt-1">{s.label}</p>
                </div>
              ))}
            </div>
          )}

          <div className="flex gap-8 border-b border-gold/15 mb-10">
            {[["rolls", "Tribal Rolls"], ["documents", "Document Repository"]].map(([k, label]) => (
              <button
                key={k}
                data-testid={`dash-tab-${k}`}
                onClick={() => setTab(k)}
                className={`relative font-mono text-[11px] tracking-[0.18em] uppercase pb-4 transition-colors ${tab === k ? "text-gold" : "text-[#A19D98] hover:text-[#F5F0EB]"}`}
              >
                {label}
                {tab === k && <span className="absolute -bottom-[1px] left-0 right-0 h-[2px] bg-gold" />}
              </button>
            ))}
          </div>

          {tab === "rolls" ? <RollsPanel /> : <DocsPanel />}
        </div>
      )}
    </div>
  );
}
